import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Alert, Card, Divider, Flex, Modal, Select, Skeleton, Switch, Tag } from 'antd';
import { collect } from 'collect.js';
import useApiClient from '../../../hooks/api/useApiClient';
import usePermissions, { ManagerPermission } from '../../../hooks/auth/usePermissions';
import { FlyerTemplate, PolygraphyProductFormat } from '../../../services/exports/Constants';
import Document from '../../pdf/Document';

const AUTO_DESIGN = 'auto';

const SLOT_NUMBERS = {
  [FlyerTemplate.One]: '#1',
  [FlyerTemplate.Two]: '#2',
  [FlyerTemplate.Three]: '#3',
  [FlyerTemplate.Four]: '#4',
};

export default function AutoFlyerSettings({ products = [] }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Marketing:Polygraphy:AutoFlyerSettings' });

  const { PolygraphyManager } = useApiClient();
  const { can } = usePermissions();
  const { company } = useSelector((state) => state.currentCompany);

  // Both settings are self-service for restaurant owners and admins: choosing a
  // design is a setting, not a paid order. The server gate is authoritative.
  const canManage = can(ManagerPermission.WriteMarketing);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [eligible, setEligible] = useState(false);
  const [approvedDesignId, setApprovedDesignId] = useState(null);
  const [approvedDesignInvalid, setApprovedDesignInvalid] = useState(false);
  const [confirmOffOpen, setConfirmOffOpen] = useState(false);

  // Human-readable options built from the flyer's editor slot (#1–#4 / Custom)
  // and its headline — the raw `format` enum ("flyer_A6") is meaningless to
  // owners. Labels are de-duplicated by appending the id on collision.
  const flyerOptions = useMemo(() => {
    const flyers = collect(products)
      .filter((item) => [PolygraphyProductFormat.FlyerA5, PolygraphyProductFormat.FlyerA6].includes(item?.format))
      .toArray();

    const baseLabelFor = (flyer) => {
      const prefix = SLOT_NUMBERS[flyer.template]
        ? t('approved_design.flyer_slot', { slot: SLOT_NUMBERS[flyer.template] })
        : t('approved_design.custom');
      const headline = flyer.data?.header || flyer.name || t('approved_design.untitled');

      return `${prefix} — ${headline}`;
    };

    const labelCounts = collect(flyers)
      .map((flyer) => baseLabelFor(flyer))
      .countBy((label) => label)
      .all();

    return flyers.map((flyer) => {
      const base = baseLabelFor(flyer);

      return {
        value: flyer.id,
        label: labelCounts[base] > 1 ? `${base} (#${flyer.id})` : base,
        file_url: flyer.file_url,
      };
    });
  }, [products, t]);

  const selectedFlyer = useMemo(
    () => collect(products).first((item) => item.id === approvedDesignId) ?? null,
    [products, approvedDesignId],
  );

  useEffect(() => {
    if (canManage) {
      loadSettings();
    }
  }, []);

  async function loadSettings() {
    setLoading(true);
    const { success, data } = await PolygraphyManager.getAutoOrderSettings();
    setLoading(false);

    if (!success) {
      toast.error(t('toasts.failed_to_load'));

      return;
    }

    setEnabled(data.auto_flyers_enabled);
    setEligible(data.eligible ?? false);
    setApprovedDesignId(data.approved_design_id ?? null);
    setApprovedDesignInvalid(data.approved_design_invalid ?? false);
  }

  function onToggleOptOut(checked) {
    // Turning auto-flyers OFF is a deliberate action — confirm first. Turning it
    // back ON is harmless, so persist immediately.
    if (!checked) {
      setConfirmOffOpen(true);

      return;
    }

    persistOptOut(true);
  }

  async function persistOptOut(nextEnabled) {
    setSaving(true);
    const previous = enabled;
    setEnabled(nextEnabled);

    const { success } = await PolygraphyManager.updateAutoFlyerOptOut({ enabled: nextEnabled });
    setSaving(false);

    if (!success) {
      setEnabled(previous);
      toast.error(t('toasts.failed_to_save'));

      return;
    }

    toast.success(t('toasts.saved'));
  }

  function confirmTurnOff() {
    setConfirmOffOpen(false);
    persistOptOut(false);
  }

  async function onChangeApprovedDesign(value) {
    const polygraphyProductId = value === AUTO_DESIGN ? null : value;
    setSaving(true);
    const previous = approvedDesignId;
    setApprovedDesignId(polygraphyProductId);

    const { success } = await PolygraphyManager.updateApprovedDesign({ polygraphy_product_id: polygraphyProductId });
    setSaving(false);

    if (!success) {
      setApprovedDesignId(previous);
      toast.error(t('toasts.failed_to_save'));

      return;
    }

    // A successful save is server-validated as a valid franchise flyer (or a
    // clear), so any stale invalid-design warning no longer applies.
    setApprovedDesignInvalid(false);
    toast.success(t('toasts.saved'));
  }

  if (!canManage) {
    return null;
  }

  // Only flyer-eligible locations can receive the auto-flyer, so the whole card
  // is hidden for ineligible ones (A3). Kept mounted while loading so the
  // eligibility check has resolved before we decide.
  if (!loading && !eligible) {
    return null;
  }

  return (
    <Card className="mb-md">
      {loading && <Skeleton active paragraph={{ rows: 2 }} />}
      {!loading && (
        <Flex vertical>
          <Flex justify="space-between" align="center" gap="middle">
            <div>
              <Flex align="center" gap="small">
                <p className="font-semibold mb-0">{t('title')}</p>
                <Tag color={enabled ? 'green' : 'default'}>{enabled ? t('status.enabled') : t('status.disabled')}</Tag>
              </Flex>
              <p className="text-secondary mb-0">{enabled ? t('opt_out.on_hint') : t('opt_out.off_hint')}</p>
            </div>
            <Switch checked={enabled} loading={saving} onChange={onToggleOptOut} />
          </Flex>

          {enabled && (
            <>
              <Divider className="my-md" />
              <Flex justify="space-between" align="flex-start" gap="middle">
                <p className="font-semibold mb-0 mt-xs">{t('approved_design.label')}</p>
                <Flex vertical align="flex-end" gap="small" className="min-w-0">
                  {approvedDesignInvalid && (
                    <Alert type="warning" showIcon message={t('approved_design.invalid_warning')} />
                  )}
                  <Select
                    style={{ width: 260 }}
                    value={approvedDesignId ?? AUTO_DESIGN}
                    disabled={saving}
                    onChange={onChangeApprovedDesign}
                    options={[{ value: AUTO_DESIGN, label: t('approved_design.auto_option') }, ...flyerOptions]}
                  />
                  {selectedFlyer?.file_url && (
                    <div className="w-fit rounded-md border border-gray-200 p-xs" data-testid="approved-design-preview">
                      <Document file={selectedFlyer.file_url} defaultScale={0.5} hideControls />
                    </div>
                  )}
                </Flex>
              </Flex>
            </>
          )}
        </Flex>
      )}

      <Modal
        open={confirmOffOpen}
        title={t('opt_out.confirm_off.title')}
        okText={t('opt_out.confirm_off.confirm')}
        cancelText={t('opt_out.confirm_off.cancel')}
        okButtonProps={{ danger: true }}
        onOk={confirmTurnOff}
        onCancel={() => setConfirmOffOpen(false)}
      >
        <p>{t('opt_out.confirm_off.body', { location: company?.name ?? '' })}</p>
      </Modal>
    </Card>
  );
}
